import { motion } from "framer-motion";
import { Radio, ShieldCheck, Users, Send, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SectionHeading } from "@/components/SectionHeading";
import { PhoneMockup } from "@/components/PhoneMockup";
import { SCREENS } from "@/data/content";

const ICONS = [Radio, ShieldCheck, Users, Send];

// Stylised live map: participants as pulsing markers on a grid
const DOTS = [
  { x: 18, y: 32, d: 0 },
  { x: 41, y: 58, d: 0.6 },
  { x: 63, y: 26, d: 1.2 },
  { x: 76, y: 66, d: 0.3 },
  { x: 30, y: 78, d: 1.6 },
  { x: 55, y: 44, d: 0.9 },
];

const LiveMap = () => (
  <div className="absolute inset-0 overflow-hidden rounded-[2.5rem]" aria-hidden="true">
    <div
      className="absolute inset-0 opacity-[0.35]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(184,180,137,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(184,180,137,0.12) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
      }}
    />
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
      <path
        d="M8 40 C 20 30, 30 20, 45 24 S 70 40, 80 30 S 95 20, 98 26"
        fill="none"
        stroke="rgba(184,180,137,0.25)"
        strokeWidth="0.4"
        strokeDasharray="1 1.2"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d="M12 70 C 25 62, 35 75, 50 68 S 72 58, 88 72"
        fill="none"
        stroke="rgba(184,180,137,0.18)"
        strokeWidth="0.4"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d="M20 20 L 62 14 L 84 46 L 70 84 L 26 78 Z"
        fill="rgba(127,121,57,0.06)"
        stroke="rgba(127,121,57,0.5)"
        strokeWidth="0.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
    {DOTS.map((p, i) => (
      <div key={i} className="absolute" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
        <span className="ping-soft absolute -inset-2 rounded-full bg-[var(--sage)]/40" style={{ animationDelay: `${p.d}s` }} />
        <span className="relative block h-2.5 w-2.5 rounded-full bg-[var(--sage)] ring-2 ring-[var(--bg)]" />
      </div>
    ))}
    <div className="absolute inset-0 bg-gradient-to-r from-[var(--surface)] via-[var(--surface)]/70 to-transparent" />
    <div className="absolute inset-0 bg-gradient-to-t from-[var(--surface)] via-transparent to-[var(--surface)]/60" />
  </div>
);

export const LiveHunt = () => {
  const { t } = useLanguage();
  const s = t.live;

  return (
    <section id="live-jagd" className="relative scroll-mt-24 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="card relative overflow-hidden rounded-[2.5rem] bg-[var(--surface)]"
        >
          <LiveMap />
          <div className="relative grid items-center gap-12 px-6 py-14 sm:px-10 lg:grid-cols-12 lg:px-16 lg:py-20">
            <div className="lg:col-span-7">
              <SectionHeading index={s.index} eyebrow={s.eyebrow} title={s.title} body={s.body} />
              <ul className="mt-9 grid gap-4 sm:grid-cols-2">
                {s.points.map((p, i) => {
                  const Icon = ICONS[i] || Check;
                  return (
                    <li key={p} className="flex items-start gap-3 rounded-2xl border border-[var(--line)] bg-[var(--bg)]/50 p-4 backdrop-blur">
                      <Icon className="mt-0.5 h-4.5 w-4.5 shrink-0 text-[var(--sage)]" />
                      <span className="text-sm leading-relaxed text-[var(--ink)]">{p}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-[var(--line-strong)] px-4 py-2">
                <span className="relative flex h-2 w-2">
                  <span className="ping-soft absolute inline-flex h-full w-full rounded-full bg-[#5fbf7a]" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#5fbf7a]" />
                </span>
                <span className="font-mono text-[11px] uppercase tracking-widest text-[var(--ink-2)]">
                  {s.stat.label}: <span className="text-[var(--ink)]">{s.stat.value}</span>
                </span>
              </div>
            </div>
            <div className="flex justify-center lg:col-span-5 lg:justify-end">
              <PhoneMockup src={SCREENS.liveJagd} alt={s.screenLabel} label={s.screenLabel} className="w-[250px] sm:w-[280px]" testId="live-phone" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
